import React, { useEffect, useState } from "react";
import { plyxApi as api } from "@/lib/plyxApi";
import { Sparkles } from "lucide-react";

/**
 * LiveHourChip — minimal countdown pill shown ONLY when the Plyx Live Hour
 * window is active. Designed to overlay the broadcaster/viewer stage so
 * tippers know their coins are doubled on the leaderboard without leaving
 * the stream.
 *
 * Polls `/api/plyx/live-hour` every 60s; ticks the countdown locally every
 * second between polls. Renders nothing while inactive.
 */
const fmtCountdown = (secs) => {
  if (secs <= 0) return "0:00";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const LiveHourChip = ({ className = "", roomName = null }) => {
  const [state, setState] = useState(null);
  const [miniState, setMiniState] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let mounted = true;
    const load = () => {
      api.get("/plyx/live-hour").then((r) => mounted && setState(r.data)).catch(() => {});
      if (roomName) {
        api.get(`/plyx/live/${encodeURIComponent(roomName)}/mini-hour`)
          .then((r) => mounted && setMiniState(r.data))
          .catch(() => {});
      }
    };
    load();
    const poll = setInterval(load, 60_000);
    const cd = setInterval(() => mounted && setTick((t) => t + 1), 1000);
    return () => { mounted = false; clearInterval(poll); clearInterval(cd); };
  }, [roomName]);

  if (!state) return null;

  const remaining = Math.max(0, (state.seconds_remaining || 0) - tick);
  const miniRemaining = Math.max(0, (miniState?.seconds_remaining || 0) - tick);
  const miniActive = !!miniState?.active && miniRemaining > 0;
  const weeklyActive = !!state.active;

  // Compose displayed multiplier — cap at 5× to match backend.
  const combined = Math.min(
    5.0,
    (weeklyActive ? state.multiplier : 1.0) * (miniActive ? miniState.multiplier : 1.0)
  );

  if (!weeklyActive && !miniActive) return null;

  // Pick whichever countdown is shorter (next thing to expire).
  const countdown = miniActive && (!weeklyActive || miniRemaining < remaining) ? miniRemaining : remaining;
  const label = miniActive && !weeklyActive ? "MINI HOUR" : "LIVE HOUR";

  return (
    <div
      data-testid="plyx-live-hour-chip"
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#FFCC2E] via-[#FF9E2D] to-[#FF3D7F] text-black font-black text-xs shadow-[0_8px_24px_-8px_rgba(255,204,46,0.7)] ${className}`}
      title={`${label} — tips count ${combined}× on the leaderboard right now`}
    >
      <Sparkles className="w-3.5 h-3.5 animate-pulse" />
      <span>{combined}× {label}</span>
      <span className="px-1.5 py-0.5 rounded-full bg-black/30 text-[#FFCC2E] tabular-nums text-[10px]">
        {fmtCountdown(countdown)}
      </span>
    </div>
  );
};

export default LiveHourChip;
